import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, MapPin, Calendar, Clock, Ticket, ExternalLink, User } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon — gold for SMART, gray for regular
const createCustomIcon = (isSmart = false) => {
  const color = isSmart ? '#D4AF37' : '#9CA3AF';
  return L.divIcon({
    className: 'custom-marker-icon',
    html: `
      <div style="
        width: 32px;
        height: 42px;
        position: relative;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
      ">
        <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.164 0 0 7.164 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.164 24.836 0 16 0Z" fill="${color}"/>
          <circle cx="16" cy="14" r="6" fill="#0a0a0a"/>
        </svg>
      </div>
    `,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42],
  });
};

// Component to fit bounds to all markers
const FitBounds = ({ cinemas }) => {
  const map = useMap();
  
  useEffect(() => {
    const cinemasWithCoords = cinemas.filter(c => c.latitude && c.longitude);
    if (cinemasWithCoords.length > 0) {
      const bounds = L.latLngBounds(
        cinemasWithCoords.map(c => [c.latitude, c.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [cinemas, map]);
  
  return null;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

export const CinemaMap = ({ 
  cinemas, 
  isOpen, 
  onClose, 
  eventTitle,
  onPurchase,
  isLoggedIn = false 
}) => {
  const [selectedCinema, setSelectedCinema] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'smart' | 'regular'
  
  // All cinemas with valid coordinates
  const cinemasWithCoords = useMemo(() => 
    cinemas.filter(c => c.latitude && c.longitude),
    [cinemas]
  );

  // Counts
  const smartCount = useMemo(() => cinemasWithCoords.filter(c => c.has_smart).length, [cinemasWithCoords]);
  const regularCount = useMemo(() => cinemasWithCoords.filter(c => !c.has_smart).length, [cinemasWithCoords]);
  const hasBothTypes = smartCount > 0 && regularCount > 0;

  // Filtered cinemas based on current filter
  const filteredCinemas = useMemo(() => {
    if (filter === 'smart') return cinemasWithCoords.filter(c => c.has_smart);
    if (filter === 'regular') return cinemasWithCoords.filter(c => !c.has_smart);
    return cinemasWithCoords;
  }, [cinemasWithCoords, filter]);
  
  // Calculate center of all cinemas
  const center = useMemo(() => {
    if (cinemasWithCoords.length === 0) return [40.4168, -3.7038];
    const avgLat = cinemasWithCoords.reduce((sum, c) => sum + c.latitude, 0) / cinemasWithCoords.length;
    const avgLng = cinemasWithCoords.reduce((sum, c) => sum + c.longitude, 0) / cinemasWithCoords.length;
    return [avgLat, avgLng];
  }, [cinemasWithCoords]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-5xl h-[80vh] bg-[#0a0a0a] border border-[var(--primary)]/30 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <MapPin className="text-[var(--primary)]" size={24} />
            <div>
              <h2 className="text-white font-['Playfair_Display'] text-xl">Mapa de Cines</h2>
              <p className="text-white/50 text-sm">{cinemasWithCoords.length} de {cinemas.length} cines con ubicación</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="map-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Filter bar — only show if there are both SMART and regular cinemas */}
        {hasBothTypes && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-white/5" data-testid="map-filter-bar">
            <FilterButton 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
              data-testid="map-filter-all"
            >
              Todos ({cinemasWithCoords.length})
            </FilterButton>
            <FilterButton 
              active={filter === 'smart'} 
              onClick={() => setFilter('smart')}
              variant="smart"
              data-testid="map-filter-smart"
            >
              Cines SMART ({smartCount})
            </FilterButton>
            <FilterButton 
              active={filter === 'regular'} 
              onClick={() => setFilter('regular')}
              variant="regular"
              data-testid="map-filter-regular"
            >
              Otros Cines ({regularCount})
            </FilterButton>
          </div>
        )}
        
        {/* Map Container */}
        <div className="flex-1 relative">
          {cinemasWithCoords.length === 0 ? (
            <div className="flex items-center justify-center h-full text-white/50">
              <div className="text-center">
                <MapPin size={48} className="mx-auto mb-4 opacity-30" />
                <p>No hay cines con coordenadas disponibles</p>
                <p className="text-sm mt-2">Añade latitud y longitud a los cines en el panel de administración</p>
              </div>
            </div>
          ) : (
            <MapContainer
              center={center}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              className="cinema-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <FitBounds cinemas={filteredCinemas} />
              
              {filteredCinemas.map((cinema, index) => (
                <Marker
                  key={`${cinema.cinema_name}-${index}-${filter}`}
                  position={[cinema.latitude, cinema.longitude]}
                  icon={createCustomIcon(cinema.has_smart)}
                  eventHandlers={{
                    click: () => setSelectedCinema(cinema),
                  }}
                >
                  <Popup className="cinema-popup">
                    <div className="min-w-[280px] p-2">
                      {/* Cinema Name + SMART badge */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <h3 className="font-['Playfair_Display'] text-xl text-[#0a0a0a] font-semibold">
                          {cinema.cinema_name}
                        </h3>
                        {cinema.has_smart && (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-[#D4AF37] text-[#0a0a0a] text-xs font-bold uppercase tracking-wide">
                            SMART
                          </span>
                        )}
                      </div>
                      
                      {/* Address/City */}
                      <div className="flex items-start gap-2 text-gray-600 text-sm mb-3">
                        <MapPin size={16} className="mt-0.5 flex-shrink-0 text-gray-500" />
                        <span>{cinema.address || cinema.city}</span>
                      </div>
                      
                      {/* Event Title */}
                      <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] px-3 py-2 mb-3">
                        <p className="font-semibold text-[#0a0a0a] text-base">{eventTitle}</p>
                      </div>
                      
                      {/* Sessions */}
                      {cinema.sessions && cinema.sessions.length > 0 && (
                        <div className="space-y-1 mb-4">
                          {cinema.sessions.filter(s => s.date || s.time).map((session, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                              {session.date && (
                                <span className="flex items-center gap-1.5">
                                  <Calendar size={14} className="text-gray-500" />
                                  {formatDate(session.date)}
                                </span>
                              )}
                              {session.time && (
                                <span className="flex items-center gap-1.5">
                                  <Clock size={14} className="text-gray-500" />
                                  {session.time}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Action Button — depends on SMART + login status */}
                      <CinemaPopupButton 
                        cinema={cinema} 
                        isLoggedIn={isLoggedIn} 
                        onPurchase={onPurchase} 
                        onClose={onClose} 
                      />
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
        
        {/* Footer with legend */}
        <div className="p-3 border-t border-white/10 flex items-center justify-between text-xs text-white/40">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#D4AF37' }} />
              SMART
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#9CA3AF' }} />
              Otros cines
            </span>
          </div>
          <span>© OpenStreetMap contributors</span>
        </div>
      </div>
      
      {/* Custom styles for the map */}
      <style>{`
        .cinema-map {
          background: #0a0a0a;
        }
        .cinema-map .leaflet-control-zoom {
          border: 1px solid rgba(212, 175, 55, 0.3);
          background: #0a0a0a;
        }
        .cinema-map .leaflet-control-zoom a {
          background: #0a0a0a;
          color: #D4AF37;
          border-bottom: 1px solid rgba(212, 175, 55, 0.2);
        }
        .cinema-map .leaflet-control-zoom a:hover {
          background: #1a1a1a;
        }
        .cinema-map .leaflet-control-attribution {
          background: rgba(10, 10, 10, 0.8);
          color: rgba(255, 255, 255, 0.4);
        }
        .cinema-map .leaflet-control-attribution a {
          color: #D4AF37;
        }
        .cinema-popup .leaflet-popup-content-wrapper {
          background: #fff;
          border-radius: 4px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          padding: 0;
        }
        .cinema-popup .leaflet-popup-content {
          margin: 0;
        }
        .cinema-popup .leaflet-popup-tip {
          background: #fff;
        }
        .cinema-popup .leaflet-popup-close-button {
          color: #666;
          font-size: 20px;
          padding: 8px;
        }
        .cinema-popup .leaflet-popup-close-button:hover {
          color: #0a0a0a;
        }
        .custom-marker-icon {
          background: none !important;
          border: none !important;
        }
        .leaflet-marker-icon {
          background: none !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

/* Filter toggle button */
function FilterButton({ active, onClick, variant = 'default', children, ...rest }) {
  let base = 'px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition-all border';
  if (variant === 'smart') {
    base += active
      ? ' bg-[#D4AF37] text-[#0a0a0a] border-[#D4AF37]'
      : ' bg-transparent text-[#D4AF37]/70 border-[#D4AF37]/30 hover:bg-[#D4AF37]/10';
  } else if (variant === 'regular') {
    base += active
      ? ' bg-[#9CA3AF] text-[#0a0a0a] border-[#9CA3AF]'
      : ' bg-transparent text-[#9CA3AF]/70 border-[#9CA3AF]/30 hover:bg-[#9CA3AF]/10';
  } else {
    base += active
      ? ' bg-white/20 text-white border-white/30'
      : ' bg-transparent text-white/50 border-white/10 hover:bg-white/5';
  }
  return <button onClick={onClick} className={base} {...rest}>{children}</button>;
}

/* Popup action button — SMART cinemas show Register/Buy, regular show web link */
function CinemaPopupButton({ cinema, isLoggedIn, onPurchase, onClose }) {
  // SMART cinema: show "Regístrate" if not logged in, "Comprar" if logged in
  if (cinema.has_smart) {
    if (!isLoggedIn) {
      return (
        <a
          href="/mi-cuenta"
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#D4AF37] text-sm font-bold uppercase tracking-wide hover:bg-[#c9a432] transition-colors"
          style={{ color: '#0a0a0a' }}
          data-testid="map-register-btn"
        >
          <User size={16} style={{ color: '#0a0a0a' }} />
          <span style={{ color: '#0a0a0a' }}>Regístrate</span>
        </a>
      );
    }
    // Logged in SMART: buy button
    if (cinema.embed_code || cinema.ticket_url || cinema.website_url) {
      return (
        <button
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#D4AF37] text-sm font-bold uppercase tracking-wide hover:bg-[#c9a432] transition-colors cursor-pointer"
          style={{ color: '#0a0a0a' }}
          data-testid="map-buy-smart-btn"
          onClick={() => {
            if (cinema.embed_code && onPurchase) {
              onPurchase(cinema);
              onClose();
            } else {
              window.open(cinema.ticket_url || cinema.website_url, '_blank');
            }
          }}
        >
          <Ticket size={16} style={{ color: '#0a0a0a' }} />
          <span style={{ color: '#0a0a0a' }}>Comprar</span>
        </button>
      );
    }
    return null;
  }

  // Regular cinema: link to their website
  if (cinema.website_url || cinema.ticket_url) {
    return (
      <a
        href={cinema.ticket_url || cinema.website_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-transparent border border-[#9CA3AF]/40 text-sm font-medium uppercase tracking-wide hover:bg-[#9CA3AF]/10 transition-colors"
        style={{ color: '#555' }}
        data-testid="map-buy-regular-btn"
      >
        <ExternalLink size={14} style={{ color: '#555' }} />
        <span style={{ color: '#555' }}>Web del cine</span>
      </a>
    );
  }
  return null;
}

export default CinemaMap;
